import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ruler } from "lucide-react";
import { cn } from "@/lib/cn";
import { friendlyError } from "@/lib/errors";
import { Panel } from "@/components/ui/Panel";
import { toast } from "@/components/ui/toast";
import { Skeleton } from "@/components/ui/states";
import {
  fetchSpecFields,
  fetchSpecValues,
  saveSpec,
  type SpecField,
  type SpecValue,
} from "./productApi";

/**
 * Ficha tecnica de un producto o de una variante.
 *
 * El formulario entero sale de `product_spec_fields`: cada tenant define sus
 * campos y esta pantalla se adapta sin tocar codigo. Lo que se escribe se
 * valida en PostgreSQL contra el tipo, el rango y las opciones declaradas, asi
 * que aqui no se replica ninguna regla — solo se elige el control adecuado y
 * se traduce el error si la base rechaza.
 */
export function SpecsPanel({
  organizationId,
  productId,
  categoryId,
  variantId = null,
  title = "Detalles",
  /** Sin marco propio: para incrustarlo dentro de otro panel. */
  bare = false,
}: {
  organizationId: string;
  productId: string;
  categoryId: string | null;
  variantId?: string | null;
  title?: string;
  bare?: boolean;
}) {
  const qc = useQueryClient();
  const nivel = variantId ? "variant" : "product";

  const fields = useQuery({
    queryKey: ["spec-fields", organizationId, categoryId],
    queryFn: () => fetchSpecFields(organizationId, categoryId),
  });

  const values = useQuery({
    queryKey: ["spec-values", productId],
    queryFn: () => fetchSpecValues(productId),
  });

  const aplicables = useMemo(
    () => (fields.data ?? []).filter((f) => f.applies_to === nivel),
    [fields.data, nivel],
  );

  const valueOf = (field: SpecField): SpecValue | undefined =>
    (values.data ?? []).find(
      (v) => v.field_id === field.id && (v.variant_id ?? null) === variantId,
    );

  const grupos = useMemo(() => {
    const map = new Map<string, SpecField[]>();
    for (const f of aplicables) {
      const g = f.group_label ?? "";
      map.set(g, [...(map.get(g) ?? []), f]);
    }
    return [...map];
  }, [aplicables]);

  async function guardar(field: SpecField, value: string | boolean | string[] | null) {
    try {
      await saveSpec({ organizationId, productId, variantId, field, value });
      await qc.invalidateQueries({ queryKey: ["spec-values", productId] });
    } catch (e) {
      toast.error(friendlyError(e));
      // Se recarga para descartar lo que la base no acepto.
      await qc.invalidateQueries({ queryKey: ["spec-values", productId] });
    }
  }

  const envolver = (contenido: React.ReactNode) =>
    bare ? <>{contenido}</> : <Panel title={title}>{contenido}</Panel>;

  if (fields.isPending || values.isPending) {
    return envolver(
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
      </div>,
    );
  }

  if (aplicables.length === 0) {
    // Incrustado, callarse es mejor que ocupar sitio con un aviso.
    if (bare) return null;

    return envolver(
      <div className="flex flex-col items-center gap-2 py-4 text-center">
        <Ruler className="size-5 text-ink-3" />
        <p className="text-ui text-ink-2">
          {nivel === "variant"
            ? "No hay campos definidos para variantes"
            : "No hay campos definidos"}
        </p>
        <p className="max-w-[34ch] text-xs text-ink-3">
          Los campos adicionales los define cada tienda según su rubro.
        </p>
      </div>,
    );
  }

  return envolver(
    <div className="flex flex-col gap-5">
      {grupos.map(([grupo, campos]) => (
        <div key={grupo} className="flex flex-col gap-3.5">
          {grupo && (
            <span className="font-mono text-mark uppercase tracking-[0.12em] text-ink-3">
              {grupo}
            </span>
          )}
          {campos.map((f) => (
            <SpecInput key={f.id} field={f} value={valueOf(f)} onSave={guardar} />
          ))}
        </div>
      ))}
    </div>,
  );
}

/* ------------------------------------------------------------------ campos */

function SpecInput({
  field,
  value,
  onSave,
}: {
  field: SpecField;
  value: SpecValue | undefined;
  onSave: (field: SpecField, value: string | boolean | string[] | null) => Promise<void>;
}) {
  const inicial =
    field.data_type === "number"
      ? (value?.value_number ?? "")
      : field.data_type === "boolean"
        ? (value?.value_bool ?? false)
        : field.data_type === "multiselect"
          ? (value?.value_text_array ?? [])
          : field.data_type === "date"
            ? (value?.value_date ?? "")
            : (value?.value_text ?? "");

  const [local, setLocal] = useState<string | boolean | string[]>(inicial);
  const [busy, setBusy] = useState(false);

  // Se guarda al salir del campo, no en cada tecla: cada pulsacion seria una
  // escritura y una validacion en la base.
  async function commit(v: string | boolean | string[] | null) {
    setBusy(true);
    await onSave(field, v);
    setBusy(false);
  }

  const etiqueta = (
    <span className="flex items-baseline gap-1.5">
      <span className="text-meta font-medium">{field.label}</span>
      {field.required && <span className="text-xs text-crit">obligatorio</span>}
      {field.unit && <span className="font-mono text-mark text-ink-3">{field.unit}</span>}
    </span>
  );

  if (field.data_type === "multiselect") {
    const elegidas = new Set(Array.isArray(local) ? (local as string[]) : []);
    return (
      <div className="flex flex-col gap-1.5">
        {etiqueta}
        <div className="flex flex-wrap gap-1.5">
          {field.options.map((o) => {
            const on = elegidas.has(o);
            return (
              <button
                key={o}
                type="button"
                aria-pressed={on}
                disabled={busy}
                onClick={() => {
                  const siguiente = new Set(elegidas);
                  if (on) siguiente.delete(o);
                  else siguiente.add(o);
                  const arr = [...siguiente];
                  setLocal(arr as never);
                  void commit(arr.length > 0 ? (arr as never) : null);
                }}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs transition-colors",
                  on
                    ? "border-accent bg-accent-soft text-accent"
                    : "border-line bg-surface text-ink-2 hover:bg-sunk",
                )}
              >
                {o}
              </button>
            );
          })}
        </div>
        {field.help_text && <span className="text-xs text-ink-3">{field.help_text}</span>}
      </div>
    );
  }

  if (field.data_type === "color") {
    const hex = typeof local === "string" && /^#[0-9a-fA-F]{6}$/.test(local) ? local : "#000000";
    return (
      <div className="flex flex-col gap-1.5">
        {etiqueta}
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={hex}
            disabled={busy}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={(e) => void commit(e.target.value)}
            aria-label={field.label}
            className="size-8 shrink-0 cursor-pointer rounded-md border border-line bg-surface"
          />
          <input
            value={String(local ?? "")}
            disabled={busy}
            placeholder="#rrggbb"
            onChange={(e) => setLocal(e.target.value)}
            onBlur={(e) => void commit(e.target.value || null)}
            className="tnum w-28 rounded-md border border-line bg-surface px-3 py-2 text-ui"
          />
        </div>
        {field.help_text && <span className="text-xs text-ink-3">{field.help_text}</span>}
      </div>
    );
  }

  if (field.data_type === "date") {
    return (
      <label className="flex flex-col gap-1.5">
        {etiqueta}
        <input
          type="date"
          value={String(local ?? "")}
          disabled={busy}
          onChange={(e) => {
            setLocal(e.target.value);
            void commit(e.target.value || null);
          }}
          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
        />
        {field.help_text && <span className="text-xs text-ink-3">{field.help_text}</span>}
      </label>
    );
  }

  if (field.data_type === "boolean") {
    const on = local === true;
    return (
      <div className="flex items-center justify-between gap-3">
        <span>
          {etiqueta}
          {field.help_text && (
            <span className="block text-xs text-ink-3">{field.help_text}</span>
          )}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={field.label}
          disabled={busy}
          onClick={() => {
            setLocal(!on);
            void commit(!on);
          }}
          className={cn(
            "relative h-5 w-9 shrink-0 rounded-full transition-colors",
            on ? "bg-accent" : "bg-line",
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 size-4 rounded-full bg-surface transition-transform",
              on ? "translate-x-[18px]" : "translate-x-0.5",
            )}
          />
        </button>
      </div>
    );
  }

  return (
    <label className="flex flex-col gap-1.5">
      {etiqueta}

      {field.data_type === "select" ? (
        <select
          value={String(local)}
          disabled={busy}
          onChange={(e) => {
            setLocal(e.target.value);
            void commit(e.target.value || null);
          }}
          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
        >
          <option value="">Sin definir</option>
          {field.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          value={String(local)}
          disabled={busy}
          inputMode={field.data_type === "number" ? "decimal" : undefined}
          maxLength={field.max_length ?? undefined}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={(e) => {
            const actual =
              field.data_type === "number"
                ? (value?.value_number ?? "")
                : (value?.value_text ?? "");
            if (e.target.value !== String(actual)) void commit(e.target.value || null);
          }}
          className={cn(
            "w-full rounded-md border border-line bg-surface px-3 py-2 text-ui",
            field.data_type === "number" && "tnum text-right",
          )}
        />
      )}

      {field.help_text && <span className="text-xs text-ink-3">{field.help_text}</span>}
      {field.data_type === "number" && (field.min_value || field.max_value) && (
        <span className="text-xs text-ink-3">
          Entre {field.min_value ?? "0"} y {field.max_value ?? "∞"}
          {field.unit ? ` ${field.unit}` : ""}
        </span>
      )}
    </label>
  );
}
